import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Package, Search as SearchIcon, ScanLine, Minus, AlertTriangle, CheckCircle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function WorkerMagazzinoPage() {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [cantieri, setCantieri] = useState([]);
  const [loading, setLoading] = useState(false);
  const [prelievoOpen, setPrelievoOpen] = useState(false);
  const [selectedMat, setSelectedMat] = useState(null);
  const [prelievoForm, setPrelievoForm] = useState({ quantita: 1, cantiere_id: '', note: '' });
  const [scanOpen, setScanOpen] = useState(false);

  useEffect(() => {
    axios.get(`${API}/cantieri`, { withCredentials: true })
      .then(r => setCantieri(Array.isArray(r.data) ? r.data : (r.data?.items || [])))
      .catch(() => {});
  }, []);

  useEffect(() => {
    const t = setTimeout(() => {
      if (q.trim().length >= 2) doSearch();
      else if (!q.trim()) setResults([]);
    }, 300);
    return () => clearTimeout(t);
  }, [q]);

  const doSearch = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/magazzino/materiali/search`, { params: { q }, withCredentials: true });
      setResults(r.data?.items || []);
      if (r.data?.matched_via === 'ai_fallback') toast.success('Ricerca AI attivata (nessun match diretto)');
    } catch (err) { toast.error('Errore ricerca'); }
    finally { setLoading(false); }
  };

  const openPrelievo = (m) => { setSelectedMat(m); setPrelievoForm({ quantita: 1, cantiere_id: '', note: '' }); setPrelievoOpen(true); };
  const submitPrelievo = async () => {
    if (!selectedMat) return;
    if (!prelievoForm.cantiere_id) { toast.error('Seleziona un cantiere'); return; }
    if (prelievoForm.quantita <= 0) { toast.error('Quantità non valida'); return; }
    try {
      const r = await axios.post(`${API}/magazzino/movimenti`, {
        materiale_id: selectedMat.id, tipo: 'scarico',
        quantita: prelievoForm.quantita, cantiere_id: prelievoForm.cantiere_id, note: prelievoForm.note,
      }, { withCredentials: true });
      toast.success(`Prelievo registrato — nuova giacenza: ${r.data.qty_after} ${selectedMat.unita}`);
      setPrelievoOpen(false);
      doSearch();
    } catch (err) { toast.error(err.response?.data?.detail || 'Errore prelievo'); }
  };

  // Scanner camera via BarcodeDetector API (nativa Chrome/Edge Android)
  const startScanner = async () => {
    if (!('BarcodeDetector' in window)) {
      toast.error("Scanner camera non supportato dal browser. Usa il campo di ricerca digitando il codice."); return;
    }
    setScanOpen(true);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } });
      const video = document.getElementById('barcode-video');
      if (video) { video.srcObject = stream; await video.play(); }
      const detector = new window.BarcodeDetector({ formats: ['code_128', 'qr_code', 'ean_13'] });
      const interval = setInterval(async () => {
        if (!document.getElementById('barcode-video')) { clearInterval(interval); return; }
        try {
          const codes = await detector.detect(video);
          if (codes.length > 0) {
            clearInterval(interval);
            stream.getTracks().forEach(t => t.stop());
            setScanOpen(false);
            setQ(codes[0].rawValue);
          }
        } catch (_) { /* ignore */ }
      }, 400);
    } catch (err) {
      toast.error('Impossibile accedere alla camera'); setScanOpen(false);
    }
  };
  const stopScanner = () => {
    const video = document.getElementById('barcode-video');
    if (video?.srcObject) video.srcObject.getTracks().forEach(t => t.stop());
    setScanOpen(false);
  };

  return (
    <div className="space-y-4 p-4 sm:p-6" data-testid="worker-magazzino-page">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold flex items-center gap-2"><Package className="h-5 w-5 text-blue-600" />Magazzino</h1>
      </div>
      <Card className="border-slate-200">
        <CardContent className="p-3 flex gap-2 items-center">
          <div className="relative flex-1">
            <SearchIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca (es. 'cavo 2.5' o codice)" className="pl-8" data-testid="worker-mag-search" autoFocus />
          </div>
          <Button variant="outline" onClick={startScanner} data-testid="worker-mag-scan"><ScanLine className="h-4 w-4 mr-1.5" />Scansiona</Button>
        </CardContent>
      </Card>

      {loading && <p className="text-sm text-slate-400 text-center py-4">Ricerca in corso...</p>}
      {!loading && q.trim().length >= 2 && results.length === 0 && (
        <Card className="border-slate-200"><CardContent className="p-6 text-center text-slate-500"><Package className="h-10 w-10 mx-auto text-slate-300 mb-2" />Nessun materiale trovato per &quot;{q}&quot;</CardContent></Card>
      )}
      <div className="grid grid-cols-1 gap-2">
        {results.map(m => {
          const low = (m.qty_current || 0) < (m.qty_min || 0);
          const available = (m.qty_current || 0) > 0;
          return (
            <Card key={m.id} className={`border-slate-200 ${low ? 'border-l-4 border-l-red-500' : ''}`} data-testid={`worker-mag-mat-${m.id}`}>
              <CardContent className="p-3 flex items-center gap-3">
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-slate-900">{m.nome}</div>
                  <div className="text-xs text-slate-500 line-clamp-1">{m.descrizione}</div>
                  <div className="flex items-center gap-2 mt-1 flex-wrap">
                    <Badge variant="outline" className="font-mono text-[10px]">{m.barcode}</Badge>
                    {m.location && <Badge variant="outline" className="text-[10px]">📍 {m.location}</Badge>}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className={`font-bold text-lg ${!available ? 'text-red-600' : low ? 'text-amber-600' : 'text-slate-900'}`}>{m.qty_current} <span className="text-xs font-normal text-slate-500">{m.unita}</span></div>
                  <div className="text-[10px] text-slate-400">{available ? (low ? <span className="text-amber-600 flex items-center gap-1"><AlertTriangle className="h-3 w-3" />sotto scorta</span> : <span className="text-emerald-600 flex items-center gap-1"><CheckCircle className="h-3 w-3" />disponibile</span>) : <span className="text-red-600">esaurito</span>}</div>
                  <Button size="sm" onClick={() => openPrelievo(m)} disabled={!available} className="mt-2 bg-slate-900 text-white h-8" data-testid={`worker-mag-prelievo-${m.id}`}><Minus className="h-3.5 w-3.5 mr-1" />Preleva</Button>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Dialog Prelievo */}
      <Dialog open={prelievoOpen} onOpenChange={setPrelievoOpen}>
        <DialogContent className="sm:max-w-md" data-testid="worker-mag-prelievo-dialog">
          <DialogHeader><DialogTitle>Preleva: {selectedMat?.nome}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="p-3 bg-slate-50 rounded-md text-sm">Disponibili: <b>{selectedMat?.qty_current} {selectedMat?.unita}</b></div>
            <div className="space-y-1"><Label>Quantità *</Label><Input type="number" step="0.01" min="0.01" value={prelievoForm.quantita} onChange={(e) => setPrelievoForm({ ...prelievoForm, quantita: parseFloat(e.target.value) || 0 })} data-testid="worker-mag-prelievo-qty" /></div>
            <div className="space-y-1"><Label>Cantiere *</Label>
              <Select value={prelievoForm.cantiere_id} onValueChange={(v) => setPrelievoForm({ ...prelievoForm, cantiere_id: v })}>
                <SelectTrigger data-testid="worker-mag-prelievo-cantiere"><SelectValue placeholder="Seleziona cantiere" /></SelectTrigger>
                <SelectContent>{cantieri.map(c => <SelectItem key={c.id} value={c.id}>{c.name || c.nome}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1"><Label>Note</Label><Input value={prelievoForm.note} onChange={(e) => setPrelievoForm({ ...prelievoForm, note: e.target.value })} placeholder="es. lotto per quadro piano 1" /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setPrelievoOpen(false)}>Annulla</Button><Button onClick={submitPrelievo} className="bg-slate-900 text-white" data-testid="worker-mag-prelievo-submit">Conferma prelievo</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Scanner */}
      <Dialog open={scanOpen} onOpenChange={(v) => { if (!v) stopScanner(); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>Scansiona codice a barre</DialogTitle></DialogHeader>
          <div className="aspect-video bg-black rounded overflow-hidden">
            <video id="barcode-video" className="w-full h-full object-cover" playsInline muted />
          </div>
          <p className="text-xs text-slate-500 text-center">Inquadra il codice a barre del materiale</p>
        </DialogContent>
      </Dialog>
    </div>
  );
}
